/**
 * TikTok Token Refresh Module (Story 11.1, Task 4)
 *
 * Refreshes TikTok access tokens using the refresh token.
 *
 * CRITICAL differences from LinkedIn:
 * - TikTok access token expires in 24 HOURS (most aggressive in system)
 * - TikTok MAY return a NEW refresh_token on each refresh — always store it
 * - LinkedIn refresh token counts down (never resets); TikTok may rotate
 * - Uses `client_key` (NOT `client_id`)
 * - Content-Type: application/x-www-form-urlencoded (same as LinkedIn)
 */

import { safeFetch, sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Constants
// ============================================================================

const TIKTOK_TOKEN_URL = "https://open.tiktokapis.com/v2/oauth/token/";
const TOKEN_REFRESH_TIMEOUT_MS = 25_000;

// ============================================================================
// Types
// ============================================================================

export interface TikTokRefreshResponse {
  access_token: string;
  expires_in: number;
  open_id: string;
  refresh_token: string;
  refresh_expires_in: number;
  scope: string;
  token_type: string;
}

// ============================================================================
// Token Refresh (Task 4.1)
// ============================================================================

/**
 * Refreshes a TikTok access token using the refresh token.
 *
 * CRITICAL: Response MAY return a NEW refresh_token.
 * Always store the returned refresh_token value, even if it looks the same.
 *
 * CRITICAL: TikTok 24h access token is the most aggressive in our system —
 * proactive refresh via cron is essential.
 *
 * @returns The parsed response with new access_token and potentially new refresh_token
 * @throws Error if the refresh fails
 */
export async function refreshTikTokAccessToken(
  refreshToken: string,
  clientKey: string,
  clientSecret: string
): Promise<TikTokRefreshResponse> {
  const body = new URLSearchParams({
    client_key: clientKey,
    client_secret: clientSecret,
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  });

  const result = await safeFetch(
    TIKTOK_TOKEN_URL,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: body.toString(),
    },
    TOKEN_REFRESH_TIMEOUT_MS
  );

  if (!result.success) {
    throw new Error(
      sanitizeErrorMessage(`TikTok token refresh failed: ${result.error.message}`)
    );
  }

  const response = result.data;
  if (!response.ok) {
    let errorMessage = `TikTok token refresh failed: HTTP ${response.status}`;
    try {
      const errorBody = (await response.json()) as {
        error?: string;
        error_description?: string;
      };
      if (errorBody.error_description) {
        errorMessage = `TikTok token refresh failed: ${errorBody.error_description}`;
      }
    } catch {
      // ignore parse errors
    }
    throw new Error(sanitizeErrorMessage(errorMessage));
  }

  const data = (await response.json()) as TikTokRefreshResponse;

  if (!data.access_token || !data.expires_in) {
    throw new Error("Invalid TikTok refresh response: missing access_token or expires_in");
  }

  if (!data.refresh_token || !data.refresh_expires_in) {
    throw new Error("Invalid TikTok refresh response: missing refresh_token or refresh_expires_in");
  }

  return data;
}
